/**
 * Persist the booking listing state (dates + guests) across visits.
 *
 * Stored in localStorage with a 7-day TTL so a user who lands on /book,
 * picks dates, gets distracted, and comes back the next day can pick up
 * where they left off — without having to re-tap the calendar.
 *
 * Best-effort; quietly no-ops if storage is unavailable (private mode,
 * over quota). Never stores PII.
 */

const STORAGE_KEY = 'qda_booking_draft_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function safeStorage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function saveBookingDraft({ checkIn, checkOut, guestCount }) {
  const s = safeStorage();
  if (!s) return;
  try {
    const data = {};
    if (checkIn) data.checkIn = checkIn;
    if (checkOut) data.checkOut = checkOut;
    if (typeof guestCount === 'number' && guestCount > 0) data.guestCount = guestCount;
    if (Object.keys(data).length === 0) return;
    s.setItem(STORAGE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* noop */ }
}

export function loadBookingDraft() {
  const s = safeStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > MAX_AGE_MS) {
      s.removeItem(STORAGE_KEY);
      return null;
    }
    const today = new Date().toISOString().split('T')[0];
    // Drop if the dates are in the past
    if (parsed.data?.checkIn && parsed.data.checkIn < today) {
      s.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.data || null;
  } catch {
    return null;
  }
}

export function clearBookingDraft() {
  const s = safeStorage();
  if (!s) return;
  try { s.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
